import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import 'leaflet/dist/leaflet.css';

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

// Fallback center (India) — only used if geolocation fails
const FALLBACK_CENTER: [number, number] = [20.5937, 78.9629];
const DEFAULT_ZOOM = 14;

interface MapProps {
  center?: [number, number];
  zoom?: number;
  children?: ReactNode;
}

function MapController({ center }: { center?: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, map.getZoom(), { duration: 1 });
    }
  }, [center, map]);
  return null;
}

/** Hook to get user's current location as map fallback */
function useMyLocation(): [number, number] | null {
  const [loc, setLoc] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setLoc([pos.coords.latitude, pos.coords.longitude]),
      () => { /* ignore — will use fallback */ },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
    );
  }, []);

  return loc;
}

export function Map({ center, zoom, children }: MapProps) {
  const myLocation = useMyLocation();
  const mapCenter = center || myLocation || FALLBACK_CENTER;

  return (
    <MapContainer
      center={mapCenter}
      zoom={zoom || DEFAULT_ZOOM}
      style={{ height: '100%', width: '100%' }}
      zoomControl={false}
    >
      <TileLayer url={TILE_URL} attribution={ATTRIBUTION} />
      <MapController center={center || myLocation || undefined} />
      {children}
    </MapContainer>
  );
}
